const DEFAULT_BAR_COUNT = 96;

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, Number.isFinite(value) ? value : min));
}

/** Convert raw PCM samples into normalized peak values suitable for a timeline. */
export function sampleAudioWaveform(
  samples: ArrayLike<number>,
  barCount = DEFAULT_BAR_COUNT
): number[] {
  const safeBarCount = Math.max(1, Math.floor(barCount));
  if (samples.length === 0) return Array.from({ length: safeBarCount }, () => 0.08);

  const peaks = Array.from({ length: safeBarCount }, (_, barIndex) => {
    const start = Math.floor((barIndex / safeBarCount) * samples.length);
    const end = Math.max(start + 1, Math.floor(((barIndex + 1) / safeBarCount) * samples.length));
    let peak = 0;
    for (let sampleIndex = start; sampleIndex < Math.min(end, samples.length); sampleIndex += 1) {
      peak = Math.max(peak, Math.abs(Number(samples[sampleIndex]) || 0));
    }
    return peak;
  });

  const maxPeak = Math.max(0.001, ...peaks);
  return peaks.map((peak) => clamp(0.08 + (peak / maxPeak) * 0.92, 0.08, 1));
}

/** Stable visual fallback for remote tracks that cannot be decoded because of CORS. */
export function fallbackAudioWaveform(seed: string, barCount = DEFAULT_BAR_COUNT): number[] {
  let hash = 2166136261;
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return Array.from({ length: Math.max(1, Math.floor(barCount)) }, (_, index) => {
    hash += 0x6d2b79f5;
    let value = hash;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    const random = ((value ^ (value >>> 14)) >>> 0) / 4294967296;
    const envelope = 0.62 + Math.sin((index / Math.max(1, barCount - 1)) * Math.PI) * 0.38;
    return clamp((0.18 + random * 0.82) * envelope, 0.08, 1);
  });
}
